/**
 * This is a custom badge component extended from ShadedUI
 *
 * @see https://ui.shadcn.com/docs/components/badge
 */

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"

import { cn } from "../../lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent font-semibold bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent font-semibold bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent font-semibold bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outlineRed:
          "border border-red-500 font-semibold bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800",
        outlineGreen:
          "border border-green-500 font-semibold bg-green-50 text-green-700 hover:bg-green-100 hover:text-green-800",
        success:
          "border-transparent font-semibold bg-green-500 text-white hover:bg-green-600",
        outlineSuccess:
          "border border-green-500 font-semibold bg-green-50 text-green-700 hover:bg-green-100 hover:text-green-800",
        outlinePrimary:
          "border border-orange-500 font-semibold bg-orange-50 text-orange-700 hover:bg-orange-100 hover:text-orange-800",
        outline: "border font-semibold border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100 hover:text-gray-800",
        blue: "bg-blue-100 font-semibold text-blue-700 border border-blue-200",
        outlinePurple: "border font-semibold border-purple-500 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:text-purple-800",
        outlineBlue: "border font-semibold border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800",
        outlineYellow: "border font-semibold border-yellow-500 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 hover:text-yellow-800",
        outlineGray: "border font-semibold border-gray-500 bg-gray-50 text-gray-700 hover:bg-gray-100 hover:text-gray-800",
        outlineSky: "border font-semibold border-sky-500 bg-sky-50 text-sky-700 hover:bg-sky-100 hover:text-sky-800",
        outlineAmber: "border font-semibold border-amber-500 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:text-amber-800",
        outlineIndigo: "border font-semibold border-indigo-500 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-800",
        customGray: "border border-custom-border text-custom-text-secondary hover:bg-gray-50 hover:text-custom-text-primary",
        gradient:
          "border-transparent font-medium bg-gradient-skill-badge text-white",
        chip:
          "rounded-lg py-1.5 px-3 border border-gray-200 bg-white font-normal text-gray-500 hover:border-gray-300 hover:text-gray-900",
        chipActive:
          "rounded-lg py-1.5 px-3 border border-orange-500 bg-orange-50 font-normal text-orange-600",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /** When provided, renders a dismiss (X) button that calls this callback. */
  onDismiss?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

function Badge({ className, variant, onDismiss, children, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), onDismiss && "gap-1 pr-1.5", className)} {...props}>
      {children}
      {onDismiss && (
        <button
          type="button"
          aria-label="Dismiss"
          className="inline-flex items-center justify-center rounded-full p-0.5 opacity-70 hover:opacity-100 transition-opacity"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={onDismiss}
        >
          <X className="size-3" />
        </button>
      )}
    </div>
  )
}

export { Badge, badgeVariants }
